/**
 * OutcomeSummaryCard — Phase 4.5
 *
 * Surfaces a participant-scoped Outcomes summary on the Participant Chart
 * AND includes a trajectory sparkline using the last 8 outcome snapshots
 * across all goals. Renders only when `goal_outcomes_reporting_enabled`
 * is ON (parent checks the flag in localStorage before mounting).
 */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, Award, Target } from 'lucide-react';
import {
  LineChart, Line, ResponsiveContainer, Tooltip, YAxis,
} from 'recharts';

export function OutcomeSummaryCard({ participantId }) {
  const [summary, setSummary] = useState(null);
  const [trajectory, setTrajectory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!participantId) return;
    let cancelled = false;
    setLoading(true); setError(null);
    api.get(`/participants/${participantId}/outcomes-report`)
      .then(r => {
        if (cancelled) return;
        const payload = r.data.data || {};
        setSummary(payload.summary || null);
        const rows = (payload.outcomes || []).slice(0, 8).reverse();
        setTrajectory(rows.map((o, i) => ({
          i, pct: o.achievement_pct ?? 0,
          type: o.measurement_type,
          at: String(o.recorded_at).slice(0, 10),
        })));
      })
      .catch(err => {
        if (cancelled) return;
        setSummary(null);
        setTrajectory([]);
        setError(err?.response?.status === 404
          ? 'Outcomes & Reporting not enabled'
          : 'Unable to load outcomes summary');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [participantId]);

  if (loading) {
    return (
      <Card data-testid="outcome-summary-card-loading">
        <CardContent className="p-4 text-xs text-muted-foreground">
          Loading outcomes summary…
        </CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card data-testid="outcome-summary-card-error">
        <CardContent className="p-4 text-xs text-red-600">{error}</CardContent>
      </Card>
    );
  }
  if (!summary || summary.total_outcomes === 0) {
    return (
      <Card data-testid="outcome-summary-card-empty">
        <CardContent className="p-4 space-y-1">
          <div className="flex items-center gap-2">
            <Award className="h-4 w-4 text-primary" />
            <h3 className="font-semibold text-sm">Outcomes Summary</h3>
          </div>
          <p className="text-xs text-muted-foreground">
            No outcomes recorded yet. Use the &ldquo;+ Outcome&rdquo; action on a goal to record the first measurement.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="outcome-summary-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Award className="h-4 w-4 text-primary" />
          <h3 className="font-semibold text-sm">Outcomes Summary</h3>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div data-testid="outcome-total">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Total recorded</p>
            <p className="text-lg font-semibold">{summary.total_outcomes}</p>
          </div>
          <div data-testid="outcome-avg-pct">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Avg achievement</p>
            <p className="text-lg font-semibold">{summary.achievement_avg_pct}%</p>
          </div>
          <div data-testid="outcome-rate">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Achieved (100%) rate</p>
            <p className="text-lg font-semibold">{summary.achievement_rate_pct}%</p>
          </div>
        </div>
        {trajectory.length > 1 ? (
          <div className="space-y-1" data-testid="outcome-trajectory-sparkline">
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <TrendingUp className="h-3 w-3" /> Last {trajectory.length} outcomes trajectory
            </div>
            <div className="h-12 w-full">
              <ResponsiveContainer>
                <LineChart data={trajectory}>
                  <YAxis hide domain={[0, 100]} />
                  <Tooltip
                    formatter={(v) => [`${v}%`, 'achievement']}
                    labelFormatter={(_, p) => p?.[0]?.payload?.at || ''}
                  />
                  <Line type="monotone" dataKey="pct" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <p className="text-[10px] text-muted-foreground" data-testid="outcome-trajectory-empty">
            Trajectory will appear after at least 2 outcomes are recorded.
          </p>
        )}
        <div className="flex flex-wrap gap-1 pt-1">
          {Object.entries(summary.by_measurement_type || {}).map(([k, v]) => (
            <span key={k} data-testid={`outcome-bucket-${k}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted text-[10px]">
              <Target className="h-2.5 w-2.5" /> {k}: {v}
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/** AchievementDashboardTile — 90-day org-wide tile.
 *  Renders with loading / empty / error states. Parent component must
 *  ensure `goal_outcomes_reporting_enabled` is ON before mounting. */
export function AchievementDashboardTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    api.get('/reports/outcomes-summary?days=90')
      .then(r => { if (!cancelled) setData(r.data.data); })
      .catch(err => {
        if (cancelled) return;
        setData(null);
        setError(err?.response?.status === 404
          ? 'Outcomes & Reporting not enabled'
          : 'Unable to load achievement metrics');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card data-testid="achievement-90d-tile-loading">
        <CardContent className="p-4 text-xs text-muted-foreground">
          Loading goal achievement…
        </CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card data-testid="achievement-90d-tile-error">
        <CardContent className="p-4 text-xs text-red-600">{error}</CardContent>
      </Card>
    );
  }
  if (!data || data.total_outcomes === 0) {
    return (
      <Card data-testid="achievement-90d-tile-empty">
        <CardContent className="p-4 space-y-1">
          <div className="flex items-center gap-2">
            <Award className="h-4 w-4 text-primary" />
            <h3 className="font-semibold text-sm">Goal Achievement (90d)</h3>
          </div>
          <p className="text-xs text-muted-foreground">
            No outcomes recorded in the last 90 days.
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card data-testid="achievement-90d-tile">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center gap-2">
          <Award className="h-4 w-4 text-primary" />
          <h3 className="font-semibold text-sm">Goal Achievement (90d)</h3>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <p className="text-[10px] uppercase text-muted-foreground">Total</p>
            <p className="text-xl font-semibold" data-testid="ach-tile-total">{data.total_outcomes}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-muted-foreground">Avg %</p>
            <p className="text-xl font-semibold" data-testid="ach-tile-avg">{data.achievement_avg_pct}%</p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-muted-foreground">Achieved rate</p>
            <p className="text-xl font-semibold" data-testid="ach-tile-rate">{data.achievement_rate_pct}%</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
